import React, { useState, useEffect, useMemo } from "react";
import { FiX, FiLink, FiPlus, FiSearch, FiCheck, FiLayers, FiInfo } from "react-icons/fi";
import { toast } from "react-toastify";
import {
  linkSubclassesToClassGroup,
  createClassGroup,
  extractApiErrorMessage,
  getClassGroupId,
} from "../../utils/classGroupApi";
import {
  DEFAULT_ARM_LABEL,
  pluralize as formatPlural,
  splitArmName,
} from "../../utils/classGroupHelpers";

// Smart arm label guesser
const guessArmLabel = (className, index = 0) => {
  if (!className) return String.fromCharCode(65 + (index % 26));
  const trimmed = className.trim();

  // "JSS 1A" -> "A", "Primary 2 Gold" -> "GOLD" (a letter/word right after the level number)
  const split = splitArmName(trimmed);
  if (split) return split.label;

  // Check if class ends with a word/letter after a space/hyphen/slash
  // e.g. "Primary 1 - Gold" -> "Gold", "JSS 1 A" -> "A", "Grade 2 Blue" -> "Blue"
  const suffixMatch = trimmed.match(/(?:[\s\-_/]+)([A-Za-z0-9]+)$/);
  if (suffixMatch && suffixMatch[1]) {
    const candidate = suffixMatch[1];
    if (candidate.length <= 10 && !candidate.toLowerCase().startsWith("class")) {
      return candidate.toUpperCase();
    }
  }

  // Check if ends with a letter attached to a number, e.g. "JSS1A" -> "A"
  const trailingLetterMatch = trimmed.match(/[0-9]+([A-Za-z])$/);
  if (trailingLetterMatch && trailingLetterMatch[1]) {
    return trailingLetterMatch[1].toUpperCase();
  }

  // Fallback to index-based letter A, B, C...
  return String.fromCharCode(65 + (index % 26));
};

const LinkSubclassesModal = ({
  isOpen,
  onClose,
  schoolId,
  classGroups = [],
  availableClasses = [], // Pool of all candidate classes that could be linked
  selectedClasses = [], // Backwards-compatible prop for candidate/selected classes
  initialSelectedClasses = [], // Classes that should be pre-checked
  targetClassGroupId = "", // Preselected if opened from a specific class group
  // Shown when the target group was just created and isn't in classGroups yet.
  targetGroupName = "",
  lockTargetGroup = false,
  subclassLabel = DEFAULT_ARM_LABEL,
  onSuccess,
}) => {
  // Pool of candidate classes
  const candidatePool = useMemo(() => {
    if (availableClasses && availableClasses.length > 0) {
      return availableClasses;
    }
    if (selectedClasses && selectedClasses.length > 0) {
      return selectedClasses;
    }
    return [];
  }, [availableClasses, selectedClasses]);

  const [selectedGroupId, setSelectedGroupId] = useState(targetClassGroupId || "");
  const [isCreatingNewGroup, setIsCreatingNewGroup] = useState(false);
  const [newGroupName, setNewGroupName] = useState("");
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [rowLabels, setRowLabels] = useState({});
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Sync target class group. With no target (e.g. "Move to another group"), default to a
  // group the class isn't already in, so saving can't just re-add it to its own group.
  useEffect(() => {
    if (targetClassGroupId) {
      setSelectedGroupId(targetClassGroupId);
    } else if (classGroups.length > 0 && !selectedGroupId) {
      const currentGroupId =
        initialSelectedClasses.length === 1
          ? getClassGroupId(initialSelectedClasses[0].class_group_id)
          : null;
      const firstOther = classGroups.find((g) => g._id !== currentGroupId);
      setSelectedGroupId(firstOther?._id || "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetClassGroupId, classGroups]);

  // Initialize selection and arm labels once per modal open — deliberately NOT
  // re-run on candidatePool/availableClasses changes while open, since a caller
  // passing an unmemoized array (a fresh reference every render) would otherwise
  // wipe the user's in-progress selection on every keystroke/click-triggered re-render.
  useEffect(() => {
    if (!isOpen) return;

    // Determine initial selected IDs
    let initialSet = new Set();
    if (initialSelectedClasses && initialSelectedClasses.length > 0) {
      initialSet = new Set(initialSelectedClasses.map((c) => c._id));
    } else if (availableClasses.length === 0 && selectedClasses.length === 1) {
      // Single class passed via selectedClasses
      initialSet = new Set([selectedClasses[0]._id]);
    } else if (availableClasses.length === 0 && selectedClasses.length > 0 && !targetClassGroupId) {
      // Bulk multi-select from table passed via selectedClasses
      initialSet = new Set(selectedClasses.map((c) => c._id));
    }

    setSelectedIds(initialSet);

    // Populate default labels
    const labels = {};
    candidatePool.forEach((cls, idx) => {
      labels[cls._id] = cls.group_label || guessArmLabel(cls.class_name, idx);
    });
    setRowLabels(labels);
    setSearchQuery("");
    setError("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Filter candidate classes by search query
  const filteredClasses = useMemo(() => {
    if (!searchQuery.trim()) return candidatePool;
    const q = searchQuery.toLowerCase().trim();
    return candidatePool.filter((cls) => {
      const name = (cls.class_name || "").toLowerCase();
      const currentGid = getClassGroupId(cls.class_group_id);
      const group = classGroups.find((g) => g._id === currentGid);
      const groupName = (group?.name || "").toLowerCase();
      return name.includes(q) || groupName.includes(q);
    });
  }, [candidatePool, searchQuery, classGroups]);

  if (!isOpen) return null;

  const targetGroupObj =
    classGroups.find((g) => g._id === selectedGroupId) ||
    (targetClassGroupId && targetGroupName && selectedGroupId === targetClassGroupId
      ? { _id: targetClassGroupId, name: targetGroupName }
      : undefined);
  const pluralLabel = formatPlural(subclassLabel, 2);
  const selectedCount = selectedIds.size;
  const selectedMoveCount = candidatePool.filter(
    (cls) =>
      selectedIds.has(cls._id) &&
      getClassGroupId(cls.class_group_id) &&
      getClassGroupId(cls.class_group_id) !== selectedGroupId,
  ).length;

  const toggleSelectClass = (clsId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(clsId)) {
        next.delete(clsId);
      } else {
        next.add(clsId);
        // Ensure default arm label exists
        if (!rowLabels[clsId]) {
          const cls = candidatePool.find((c) => c._id === clsId);
          if (cls) {
            setRowLabels((prevLabels) => ({
              ...prevLabels,
              [clsId]: cls.group_label || guessArmLabel(cls.class_name, candidatePool.indexOf(cls)),
            }));
          }
        }
      }
      return next;
    });
  };

  const handleSelectAllFiltered = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      filteredClasses.forEach((cls) => next.add(cls._id));
      return next;
    });
  };

  const handleDeselectAllFiltered = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      filteredClasses.forEach((cls) => next.delete(cls._id));
      return next;
    });
  };

  const handleLabelChange = (classId, val) => {
    setRowLabels((prev) => ({
      ...prev,
      [classId]: val,
    }));
  };

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();

    if (selectedCount === 0) {
      setError(`Please select at least one ${subclassLabel.toLowerCase()} to link`);
      return;
    }

    let finalGroupId = selectedGroupId;
    let createdGroupName = "";

    try {
      setLoading(true);
      setError("");

      if (isCreatingNewGroup) {
        if (!newGroupName.trim()) {
          setError("Class group name is required");
          setLoading(false);
          return;
        }
        const createdGroup = await createClassGroup(schoolId, newGroupName.trim());
        finalGroupId = createdGroup._id;
        createdGroupName = createdGroup?.name || newGroupName.trim();
      }

      if (!finalGroupId) {
        setError("Please select or create a class group");
        setLoading(false);
        return;
      }

      const selectedClassObjects = candidatePool.filter((cls) => selectedIds.has(cls._id));

      const subclassesPayload = selectedClassObjects.map((cls, idx) => ({
        class_id: cls._id,
        group_label:
          (rowLabels[cls._id] || "").trim() ||
          cls.group_label ||
          guessArmLabel(cls.class_name, idx),
      }));

      await linkSubclassesToClassGroup(schoolId, finalGroupId, subclassesPayload);

      const resolvedGroup = classGroups.find((g) => g._id === finalGroupId) ||
        (targetGroupObj?._id === finalGroupId ? targetGroupObj : null) || {
          name: newGroupName.trim(),
        };
      const groupName = resolvedGroup?.name ? ` "${resolvedGroup.name}"` : "";

      toast.success(
        `Successfully linked ${selectedCount} ${formatPlural(subclassLabel, selectedCount).toLowerCase()} to${groupName}`,
      );

      if (onSuccess) onSuccess(finalGroupId);
      onClose();
    } catch (err) {
      console.error("Error linking subclasses:", err);
      if (createdGroupName) {
        onSuccess?.(finalGroupId);
        setError(
          `Class group "${createdGroupName}" was created, but the selected ${pluralLabel.toLowerCase()} were not linked. It is saved; open the group and try linking them again.`,
        );
      } else {
        setError(extractApiErrorMessage(err, `Failed to link ${pluralLabel.toLowerCase()}`));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-slate-900/60 backdrop-blur-sm animate-fade-in">
      <div className="bg-white rounded-2xl max-w-xl w-full shadow-2xl border border-slate-200 overflow-hidden flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 flex-shrink-0 bg-white">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-indigo-50 text-indigo-600 rounded-xl shadow-xs">
              <FiLink className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-base font-bold text-slate-900 leading-tight">
                Choose this group's {formatPlural(subclassLabel, 2).toLowerCase()}
              </h3>
              <p className="text-xs text-slate-500 mt-0.5">
                Tick the classes that belong to this level. Their students, class teacher and fees stay the same.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 p-2 rounded-xl hover:bg-slate-100 transition-colors"
          >
            <FiX className="w-5 h-5" />
          </button>
        </div>

        {/* Modal Form Content */}
        <form onSubmit={handleSubmit} className="p-6 overflow-y-auto flex-1 space-y-5">
          {error && (
            <div className="p-3.5 rounded-xl bg-rose-50 border border-rose-200 text-xs font-medium text-rose-700 flex items-center gap-2">
              <FiInfo className="w-4 h-4 flex-shrink-0 text-rose-500" />
              <span>{error}</span>
            </div>
          )}

          {/* Group Selection / Target Display */}
          <div className="space-y-2">
            <label className="block text-xs font-bold uppercase tracking-wider text-slate-600">
              Class group
            </label>

            {lockTargetGroup && targetGroupObj ? (
              <div className="p-3.5 bg-gradient-to-r from-indigo-50/80 to-purple-50/50 border border-indigo-100 rounded-xl flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <div className="p-2 bg-indigo-100/80 text-indigo-700 rounded-lg">
                    <FiLayers className="w-4 h-4" />
                  </div>
                  <div>
                    <span className="text-[11px] font-semibold text-indigo-600 uppercase tracking-wider block">
                      Adding to
                    </span>
                    <span className="text-sm font-bold text-slate-900">{targetGroupObj.name}</span>
                  </div>
                </div>
              </div>
            ) : !isCreatingNewGroup ? (
              <div className="flex items-center gap-2">
                <select
                  value={selectedGroupId}
                  onChange={(e) => setSelectedGroupId(e.target.value)}
                  disabled={lockTargetGroup}
                  className="flex-1 rounded-xl border border-slate-300 bg-white px-3.5 py-2.5 text-sm font-medium text-slate-900 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-all shadow-2xs"
                >
                  <option value="">-- Select Class Group --</option>
                  {classGroups.map((group) => (
                    <option key={group._id} value={group._id}>
                      {group.name}
                    </option>
                  ))}
                </select>

                {!targetClassGroupId && (
                  <button
                    type="button"
                    onClick={() => setIsCreatingNewGroup(true)}
                    className="inline-flex items-center gap-1.5 px-3.5 py-2.5 text-xs font-semibold text-indigo-600 hover:text-indigo-700 bg-indigo-50 hover:bg-indigo-100 rounded-xl transition-colors flex-shrink-0 border border-indigo-100"
                  >
                    <FiPlus className="w-4 h-4" />
                    New Group
                  </button>
                )}
              </div>
            ) : (
              <div className="p-4 rounded-xl bg-slate-50 border border-slate-200 space-y-2.5">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-slate-700 flex items-center gap-1.5">
                    <FiLayers className="w-3.5 h-3.5 text-indigo-600" />
                    Create New Class Group
                  </span>
                  <button
                    type="button"
                    onClick={() => setIsCreatingNewGroup(false)}
                    className="text-xs font-medium text-indigo-600 hover:text-indigo-800 underline"
                  >
                    Choose existing group
                  </button>
                </div>
                <input
                  type="text"
                  value={newGroupName}
                  onChange={(e) => setNewGroupName(e.target.value)}
                  placeholder="e.g. Primary 1 or JSS 1"
                  className="w-full rounded-xl border border-slate-300 bg-white px-3.5 py-2 text-sm text-slate-900 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 shadow-2xs"
                />
              </div>
            )}
          </div>

          {/* Subclass Selection & Arms Section */}
          <div className="space-y-3 pt-1">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-600">
                  Choose classes to add
                </label>
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-bold bg-indigo-100 text-indigo-700">
                  {selectedCount} of {candidatePool.length} selected
                </span>
              </div>

              {filteredClasses.length > 0 && (
                <div className="flex items-center gap-2 text-xs">
                  {filteredClasses.some((c) => !selectedIds.has(c._id)) ? (
                    <button
                      type="button"
                      onClick={handleSelectAllFiltered}
                      className="text-indigo-600 hover:text-indigo-800 font-semibold"
                    >
                      Select all
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={handleDeselectAllFiltered}
                      className="text-slate-500 hover:text-slate-700 font-medium"
                    >
                      Deselect all
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Search Filter when pool is larger than 3 */}
            {candidatePool.length > 3 && (
              <div className="relative">
                <FiSearch className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search classes..."
                  className="w-full rounded-xl border border-slate-200 bg-slate-50/80 pl-9 pr-8 py-1.5 text-xs text-slate-800 placeholder-slate-400 focus:bg-white focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-all"
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={() => setSearchQuery("")}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    <FiX className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            )}

            {/* List of Subclasses */}
            <div className="border border-slate-200 rounded-2xl overflow-hidden divide-y divide-slate-100 max-h-64 overflow-y-auto shadow-2xs">
              {candidatePool.length === 0 ? (
                <div className="p-8 text-center bg-slate-50">
                  <FiLayers className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                  <p className="text-xs font-semibold text-slate-600">
                    No eligible classes found
                  </p>
                  <p className="text-[11px] text-slate-400 mt-1">
                    There are no classes available to add right now.
                  </p>
                </div>
              ) : filteredClasses.length === 0 ? (
                <div className="p-6 text-center bg-slate-50">
                  <p className="text-xs text-slate-500">
                    No classes match "{searchQuery}"
                  </p>
                  <button
                    type="button"
                    onClick={() => setSearchQuery("")}
                    className="mt-1 text-xs text-indigo-600 font-semibold hover:underline"
                  >
                    Clear search
                  </button>
                </div>
              ) : (
                filteredClasses.map((cls) => {
                  const isSelected = selectedIds.has(cls._id);
                  const currentGid = getClassGroupId(cls.class_group_id);
                  const currentGroup = classGroups.find((g) => g._id === currentGid);
                  const isMovingGroup =
                    currentGid && selectedGroupId && currentGid !== selectedGroupId;

                  return (
                    <div
                      key={cls._id}
                      onClick={() => toggleSelectClass(cls._id)}
                      className={`px-4 py-3 flex items-center justify-between gap-3 transition-all cursor-pointer select-none ${
                        isSelected
                          ? "bg-indigo-50/40 hover:bg-indigo-50/60"
                          : "bg-white hover:bg-slate-50/80 opacity-75 hover:opacity-100"
                      }`}
                    >
                      {/* Checkbox and Class details */}
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        <div
                          className={`w-5 h-5 rounded-md flex items-center justify-center transition-all flex-shrink-0 ${
                            isSelected
                              ? "bg-indigo-600 text-white shadow-xs"
                              : "border-2 border-slate-300 bg-white"
                          }`}
                        >
                          {isSelected && <FiCheck className="w-3.5 h-3.5 stroke-[3]" />}
                        </div>

                        <div className="min-w-0">
                          <p
                            className={`text-sm font-semibold truncate transition-colors ${
                              isSelected ? "text-indigo-950" : "text-slate-700"
                            }`}
                          >
                            {cls.class_name}
                          </p>

                          {isMovingGroup ? (
                            <span className="inline-flex items-center gap-1 text-[11px] font-medium text-amber-700 bg-amber-50 px-2 py-0.5 rounded-md border border-amber-200/60 mt-0.5">
                              <FiInfo className="w-3 h-3 flex-shrink-0 text-amber-500" />
                              Currently in {currentGroup?.name || "another group"} · will move here
                            </span>
                          ) : currentGid ? (
                            <span className="text-[11px] text-slate-400 mt-0.5 block">
                              In {currentGroup?.name || "group"}
                            </span>
                          ) : (
                            <span className="text-[11px] text-slate-400 mt-0.5 block">
                              Not in a class group yet
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Arm / Group Label input */}
                      <div
                        className="flex items-center gap-2 flex-shrink-0"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="text-right">
                          <span className={`mb-1 block text-[10px] font-semibold uppercase tracking-wide ${isSelected ? "text-slate-500" : "text-slate-300"}`}>
                            {subclassLabel} label
                          </span>
                          <input
                            type="text"
                            maxLength={15}
                            disabled={!isSelected}
                            value={rowLabels[cls._id] || ""}
                            onChange={(e) => handleLabelChange(cls._id, e.target.value)}
                            placeholder={guessArmLabel(cls.class_name, candidatePool.indexOf(cls))}
                            title="Arm or group designation (e.g. A, B, Gold)"
                            className={`w-20 sm:w-24 text-center uppercase rounded-lg border px-2 py-1.5 text-xs sm:text-sm font-bold transition-all ${
                              isSelected
                                ? "border-slate-300 bg-white text-slate-900 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 shadow-2xs"
                                : "border-slate-200 bg-slate-100 text-slate-400 cursor-not-allowed"
                            }`}
                          />
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            <p className="text-[11px] text-slate-500 px-1">
              The label is what makes each {subclassLabel.toLowerCase()} different, e.g. A, B or Gold. We guessed it from the class name — change it if it's wrong.
            </p>
          </div>
        </form>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 px-6 py-4 border-t border-slate-100 bg-slate-50 flex-shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-xs font-semibold text-slate-600 hover:text-slate-800 hover:bg-slate-200/70 rounded-xl transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={loading || selectedCount === 0}
            className="px-5 py-2 text-xs font-bold text-white bg-indigo-600 hover:bg-indigo-700 active:bg-indigo-800 rounded-xl shadow-sm disabled:opacity-40 disabled:cursor-not-allowed transition-all flex items-center gap-2"
          >
            {loading && (
              <div className="animate-spin rounded-full h-3.5 w-3.5 border-2 border-white border-t-transparent" />
            )}
            {selectedCount > 0
              ? `${selectedMoveCount > 0 ? "Move" : "Add"} ${selectedCount} class${selectedCount !== 1 ? "es" : ""} to this group`
              : "Tick at least one class"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default LinkSubclassesModal;
